import { InfoHint } from "./Tooltip";

// The activation funnel — a VERTICAL timeline shared by the org + memex views.
// A continuous rail runs top-to-bottom with the stages spread along it
// (justify-between) so the strip STRETCHES to match the height of the donut +
// cards stack beside it. Each stage shows the date it was first reached, where
// the schema has one. It is NOT a strict dependency chain (you can connect MCP
// without defining an AC), so the rail stays neutral and a caption says so. Same
// testids (`funnel-strip`, `stage-<key>`, data-lit) regardless of caller.

export interface FunnelStage {
  key: string;
  label: string;
  hint?: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

export function FunnelStrip({
  stages,
  funnel,
  dates,
  hint,
}: {
  stages: FunnelStage[];
  funnel: Record<string, boolean>;
  dates?: Record<string, string | null>;
  hint?: string;
}) {
  return (
    <section className="flex h-full flex-col" data-testid="funnel-strip">
      <h2 className="mb-3 flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-muted">
        Activation
        {hint && <InfoHint text={hint} />}
      </h2>

      <div className="relative flex-1">
        {/* Continuous neutral rail behind the nodes. */}
        <span aria-hidden className="absolute bottom-2 left-[5px] top-2 w-px bg-edge-strong" />
        <ol className="relative flex h-full flex-col justify-between">
          {stages.map((s) => {
            const lit = funnel[s.key] ?? false;
            const at = lit ? dates?.[s.key] : null;
            return (
              <li key={s.key} className="flex items-center gap-3">
                <span
                  aria-hidden
                  className={[
                    "z-10 h-2.5 w-2.5 flex-none rounded-full",
                    lit ? "bg-status-success-text" : "border border-edge-strong bg-card-hover",
                  ].join(" ")}
                />
                <span
                  data-testid={`stage-${s.key}`}
                  data-lit={lit}
                  title={s.hint}
                  className="flex flex-col leading-tight"
                >
                  <span className={["text-sm", lit ? "text-status-success-text" : "text-muted"].join(" ")}>
                    {s.label}
                  </span>
                  {at && <span className="text-[11px] text-muted">{formatDate(at)}</span>}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      <p className="mt-2 text-[11px] text-muted">
        A rough top-to-bottom journey — stages light independently, so a later one can light
        before an earlier one (e.g. MCP connected without an AC defined).
      </p>
    </section>
  );
}
